import { IGapSuggestion } from "@/api/clause-checklists/checklist-generation.types";
import {
    adminDraftQueryKeys,
    resolveGapSuggestion,
} from "@/api/clause-checklists/drafts";
import { ActionButton } from "@/components/shared/buttons/ActionButton";
import { NewClauseDialog } from "@/pages/admin/clauses/components/dialogs/clauses/NewClauseDialog";
import { Box, Card, CardContent, Stack, Typography } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { CONFIDENCE_ORDER } from "./confidenceUtils";

interface GapAnalysisProps {
    draftId: number;
    suggestions: IGapSuggestion[];
}

export const GapAnalysis = ({ draftId, suggestions }: GapAnalysisProps) => {
    const queryClient = useQueryClient();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedSuggestion, setSelectedSuggestion] =
        useState<IGapSuggestion | null>(null);

    const resolveMutation = useMutation({
        mutationFn: ({
            gapTitle,
            clauseId,
        }: {
            gapTitle: string;
            clauseId: number;
        }) => resolveGapSuggestion(draftId, gapTitle, clauseId),
        onSuccess: () => {
            queryClient.refetchQueries({
                queryKey: adminDraftQueryKeys.detail(draftId),
            });
        },
    });

    const sorted = useMemo(
        () =>
            [...(suggestions ?? [])].sort(
                (a, b) =>
                    (CONFIDENCE_ORDER[a.confidence] ?? 3) -
                    (CONFIDENCE_ORDER[b.confidence] ?? 3)
            ),
        [suggestions]
    );

    if (!suggestions || suggestions.length === 0) {
        return null;
    }

    const handleAdd = (suggestion: IGapSuggestion) => {
        setSelectedSuggestion(suggestion);
        setDialogOpen(true);
    };

    const handleClauseCreated = (clauseId: number) => {
        if (!selectedSuggestion) return;

        resolveMutation.mutate({
            gapTitle: selectedSuggestion.title,
            clauseId,
        });
        queryClient.invalidateQueries({
            queryKey: adminDraftQueryKeys.detail(draftId),
        });
    };

    return (
        <Box>
            <Typography
                variant="subtitle1"
                fontWeight={600}
                gutterBottom
                sx={{
                    mb: 2,
                }}
            >
                Suggested Clauses Not in Library
                <Typography
                    component="span"
                    variant="subtitle2"
                    fontWeight={400}
                    color="text.secondary"
                    sx={{ ml: 1 }}
                >
                    ({suggestions.length})
                </Typography>
            </Typography>

            <Stack spacing={2}>
                {sorted.map((suggestion) => {
                    const isAdded = suggestion.resolved_clause_id != null;

                    return (
                        <Card key={suggestion.title} variant="outlined">
                            <CardContent
                                sx={{
                                    display: "flex",
                                    flexDirection: {
                                        xs: "column",
                                        sm: "row",
                                    },
                                    alignItems: {
                                        xs: "stretch",
                                        sm: "flex-start",
                                    },
                                    gap: 2,
                                    py: 2,
                                    "&:last-child": { pb: 2 },
                                }}
                            >
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                        variant="subtitle1"
                                        fontWeight={600}
                                        sx={{ mb: 0.5 }}
                                        noWrap
                                    >
                                        {suggestion.title}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {suggestion.intent}
                                    </Typography>
                                </Box>

                                <Box
                                    sx={{
                                        flexShrink: 0,
                                        alignSelf: {
                                            xs: "flex-end",
                                            sm: "center",
                                        },
                                    }}
                                >
                                    {isAdded ? (
                                        <ActionButton
                                            size="small"
                                            variant="text"
                                            disabled
                                            startIcon={
                                                <CheckCircle2 size={16} />
                                            }
                                            color="success"
                                        >
                                            Added
                                        </ActionButton>
                                    ) : (
                                        <ActionButton
                                            size="small"
                                            variant="text"
                                            startIcon={<Plus size={16} />}
                                            onClick={() =>
                                                handleAdd(suggestion)
                                            }
                                        >
                                            Add
                                        </ActionButton>
                                    )}
                                </Box>
                            </CardContent>
                        </Card>
                    );
                })}
            </Stack>

            {selectedSuggestion && (
                <NewClauseDialog
                    open={dialogOpen}
                    onClose={() => {
                        setDialogOpen(false);
                        setSelectedSuggestion(null);
                    }}
                    title={selectedSuggestion.title}
                    description={selectedSuggestion.intent}
                    initialExamples={selectedSuggestion.examples ?? []}
                    onCreated={handleClauseCreated}
                />
            )}
        </Box>
    );
};
